/**
 * Gallery V2 Hooks
 *
 * React Query Hooks für die Gallery V2 API.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useCallback, useMemo, useEffect, useRef } from 'react';
import {
  searchGallery,
  getFilterOptions,
  getFolders,
  createFolder,
  updateFolder,
  deleteFolder,
  addImagesToFolder,
  toggleFavorite,
  getSavedSearches,
  createSavedSearch,
  deleteSavedSearch,
  updateImageMetadata,
  type GallerySearchParams,
} from '@/lib/api/gallery-v2';
import { useGallerySettings } from './useGallerySettings';

// =============================================================================
// Gallery Search Hook
// =============================================================================

export function useGallerySearch() {
  const [searchParams, setSearchParams] = useSearchParams();
  const hasInitialized = useRef(false);
  const { settings } = useGallerySettings();

  // Parse URL params to GallerySearchParams
  const params: GallerySearchParams = useMemo(() => {
    const tags = searchParams.get('tags');
    const approvalStatus = searchParams.get('approval_status');

    // Default approval status basiert auf workspace setting
    let defaultApprovalStatus: Array<'pending' | 'approved' | 'rejected'> | undefined;

    if (settings?.approval_mode_enabled) {
      // Wenn Workflow aktiv: Verwende konfigurierten Default-Filter
      if (settings.default_approval_filter === 'approved_only') {
        defaultApprovalStatus = ['approved'];
      } else {
        // Default: pending_approved
        defaultApprovalStatus = ['pending', 'approved'];
      }
    } else {
      // Wenn deaktiviert: alle Bilder anzeigen (kein Filter)
      defaultApprovalStatus = undefined;
    }

    return {
      q: searchParams.get('q') || undefined,
      tags: tags ? tags.split(',').filter(Boolean) : undefined,
      template_id: searchParams.get('template_id') || undefined,
      ratio: searchParams.get('ratio') || undefined,
      format: searchParams.get('format') || undefined,
      created_by: searchParams.get('created_by') || undefined,
      folder_id: searchParams.get('folder_id') || undefined,
      favorites_only: searchParams.get('favorites_only') === 'true',
      date_from: searchParams.get('date_from') || undefined,
      date_to: searchParams.get('date_to') || undefined,
      approval_status: approvalStatus
        ? (approvalStatus.split(',').filter(Boolean) as Array<'pending' | 'approved' | 'rejected'>)
        : defaultApprovalStatus,
      min_rating: searchParams.get('min_rating') ? parseInt(searchParams.get('min_rating')!, 10) : undefined,
      sort_by:
        (searchParams.get('sort_by') as 'created_at' | 'display_name' | 'self_rating') ||
        'created_at',
      sort_order:
        (searchParams.get('sort_order') as 'asc' | 'desc') || 'desc',
      page: parseInt(searchParams.get('page') || '1', 10),
      per_page: parseInt(searchParams.get('per_page') || '24', 10),
    };
  }, [searchParams, settings]);

  // Update URL params
  const updateParams = useCallback(
    (updates: Partial<GallerySearchParams>) => {
      const newParams = new URLSearchParams(searchParams);

      Object.entries(updates).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
          newParams.delete(key);
        } else if (Array.isArray(value)) {
          if (value.length > 0) {
            newParams.set(key, value.join(','));
          } else {
            newParams.delete(key);
          }
        } else if (typeof value === 'boolean') {
          if (value) {
            newParams.set(key, 'true');
          } else {
            newParams.delete(key);
          }
        } else {
          newParams.set(key, String(value));
        }
      });

      // Reset page when filters change
      if (!('page' in updates)) {
        newParams.set('page', '1');
      }

      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  // Set default approval_status in URL if not present (only on first load)
  // Only wenn approval_mode_enabled === true
  useEffect(() => {
    if (hasInitialized.current) return;
    if (settings === undefined) return; // Warte bis Settings geladen sind

    const approvalStatusParam = searchParams.get('approval_status');
    if (!approvalStatusParam && settings.approval_mode_enabled) {
      const newParams = new URLSearchParams(searchParams);

      // Verwende konfigurierten Default-Filter
      const defaultValue = settings.default_approval_filter === 'approved_only'
        ? 'approved'
        : 'pending,approved';

      newParams.set('approval_status', defaultValue);
      setSearchParams(newParams, { replace: true });
    }

    hasInitialized.current = true;
  }, [searchParams, setSearchParams, settings]);

  // Query
  const query = useQuery({
    queryKey: ['gallery-v2', 'search', params],
    queryFn: () => searchGallery(params),
    staleTime: 30_000,
  });

  return {
    ...query,
    params,
    updateParams,
    clearFilters,
    images: query.data?.images || [],
    pagination: query.data?.pagination,
    filtersApplied: query.data?.filters_applied || {},
    approvalModeEnabled: settings?.approval_mode_enabled ?? true, // Default: true
  };
}

// =============================================================================
// Filter Options Hook
// =============================================================================

export function useFilterOptions() {
  return useQuery({
    queryKey: ['gallery-v2', 'filter-options'],
    queryFn: getFilterOptions,
    staleTime: 60_000 * 5, // 5 minutes
  });
}

// =============================================================================
// Folders Hook
// =============================================================================

export function useFolders() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['gallery-v2', 'folders'],
    queryFn: getFolders,
    staleTime: 60_000,
  });

  const createMutation = useMutation({
    mutationFn: createFolder,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gallery-v2', 'folders'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      folderId,
      data,
    }: {
      folderId: string;
      data: Parameters<typeof updateFolder>[1];
    }) => updateFolder(folderId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gallery-v2', 'folders'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteFolder,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gallery-v2', 'folders'] });
    },
  });

  const addImagesMutation = useMutation({
    mutationFn: ({
      folderId,
      imageIds,
    }: {
      folderId: string;
      imageIds: string[];
    }) => addImagesToFolder(folderId, imageIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gallery-v2'] });
    },
  });

  return {
    ...query,
    folders: query.data?.folders || [],
    createFolder: createMutation.mutateAsync,
    updateFolder: updateMutation.mutateAsync,
    deleteFolder: deleteMutation.mutateAsync,
    addImages: addImagesMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}

// =============================================================================
// Favorites Hook
// =============================================================================

export function useFavorites() {
  const queryClient = useQueryClient();

  const toggleMutation = useMutation({
    mutationFn: toggleFavorite,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gallery-v2', 'search'] });
    },
  });

  return {
    toggle: toggleMutation.mutateAsync,
    isToggling: toggleMutation.isPending,
  };
}

// =============================================================================
// Saved Searches Hook
// =============================================================================

export function useSavedSearches() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['gallery-v2', 'saved-searches'],
    queryFn: getSavedSearches,
    staleTime: 60_000,
  });

  const createMutation = useMutation({
    mutationFn: createSavedSearch,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['gallery-v2', 'saved-searches'],
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteSavedSearch,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['gallery-v2', 'saved-searches'],
      });
    },
  });

  return {
    ...query,
    searches: query.data?.saved_searches || [],
    createSearch: createMutation.mutateAsync,
    deleteSearch: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
  };
}

// =============================================================================
// Image Metadata Hook
// =============================================================================

export function useUpdateImageMetadata() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      imageId,
      data,
    }: {
      imageId: string;
      data: { display_name?: string; alt_title?: string };
    }) => updateImageMetadata(imageId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gallery-v2', 'search'] });
    },
  });
}

// =============================================================================
// Simple Gallery Hook (für SingleImageMode - ohne URL-State)
// =============================================================================

interface UseSimpleGalleryOptions {
  page?: number;
  perPage?: number;
  enabled?: boolean;
}

export function useSimpleGallery(options: UseSimpleGalleryOptions = {}) {
  const { page = 1, perPage = 12, enabled = true } = options;

  const query = useQuery({
    queryKey: ['gallery-v2', 'search', { page, per_page: perPage }],
    queryFn: () => searchGallery({ page, per_page: perPage }),
    enabled,
    staleTime: 30_000,
  });

  return {
    images: query.data?.images || [],
    pagination: query.data?.pagination,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
}
